import { useEffect, useState } from 'react';
import {
    Form,
    Container,
    Row,
    Col,
    Button,
    Stack,
    ListGroup,
    Modal,
    Card,
    Spinner,
    Badge,
} from 'react-bootstrap';
import { CircleAlert, ThumbsDown, ThumbsUp } from 'lucide-react';
import './eyetestList.scss';
import { useStatusChangeMutation } from './eyetestApi';

import LoadingComponent from '../LoadingComponent';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

const EyeTestStatus = ({ eyeTestData, setError }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [approveLoading, setApproveLoading] = useState(false);
    const [notApproveLoading, setNotApproveLoading] = useState(false);
    const [modalActive, setModalActive] = useState(false);
    const [statusChangeMutation] = useStatusChangeMutation();

    const statusHandle = async (status) => {
        // Set loading state based on which button was clicked
        if (status === 'Approved') {
            setApproveLoading(true);
        } else {
            setNotApproveLoading(true);
        }

        const { testid, comment, commentsForPrescription, testDetails, isDoctorNewComment, doctorNewComment } =
            eyeTestData || {};
        const tempData = {
            testno: testid,
            status,
            commentsForPrescription,
            comments: comment,
            isDoctorNewComment,
            doctorNewComment,
            testDetails: [testDetails?.[8], testDetails?.[9]],
        };

        const isNotApproved = status === 'Not Approved';
        const isCommentEmpty = !comment?.trim();

        if (isNotApproved) {
            if (isCommentEmpty) {
                setError((prev) => ({ ...prev, comment: true }));
                // Reset loading state
                setNotApproveLoading(false);
                return;
            }

            setError((prev) => ({ ...prev, comment: false }));

            if (!modalActive) {
                setModalActive(true);
                // Reset loading state since we're showing modal
                setNotApproveLoading(false);
                return;
            }
        }

        try {
            const { data } = await statusChangeMutation(tempData);
            if (data) {
                if (status === 'Not Approved') {
                    navigate('/eyetest/list', {
                        state: {
                            status: 'pendingValidation',
                        },
                    });
                }
                if (status === 'Approved') {
                    navigate('/eyetest/list', {
                        state: {
                            status: 'pendingValidation',
                        },
                    });
                }
            }
        } catch (error) {
            console.error('Status change error:', error);
            // Reset loading states on error
            setApproveLoading(false);
            setNotApproveLoading(false);
        }
    };

    return (
        <>
            {loading ? <LoadingComponent /> : ''}
            <Stack direction="horizontal" gap={3}>
                <div className="ms-auto">
                    <Button
                        variant="outline-danger"
                        onClick={() => statusHandle('Not Approved')}
                        disabled={approveLoading || notApproveLoading}
                    >
                        {notApproveLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {t('processing')}
                            </>
                        ) : (
                            <>
                                <ThumbsDown width={16} height={16} />{' '}
                                {t('notApproveButton')}
                            </>
                        )}
                    </Button>{' '}
                    <Button
                        onClick={() => statusHandle('Approved')}
                        disabled={approveLoading || notApproveLoading}
                    >
                        {approveLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                {t('processing')}
                            </>
                        ) : (
                            <>
                                <ThumbsUp width={16} height={16} />{' '}
                                {t('approveButton')}
                            </>
                        )}
                    </Button>
                </div>
            </Stack>

            <Modal show={modalActive} backdrop="static" keyboard={false}>
                <Modal.Body className="text-center">
                    <CircleAlert
                        size={60}
                        className="mb-4 mt-4"
                        color="#f8bb86"
                    />
                    <p>{t('areYouSure')}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setModalActive(false)}
                    >
                        {t('okay')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => statusHandle('Not Approved')}
                    >
                        {t('submitRequest')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};
export default EyeTestStatus;
